import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import Select from "react-select";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  animation: ${fadeUp} 300ms ease forwards;
`;

const ModalBox = styled.div`
  background: #fffdf9;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
  position: relative;
`;

const Title = styled.h2`
  margin-bottom: 16px;
  color: #1f2937;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  &:hover {
    color: #000;
  }
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  color: #444;
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: 10px;
  border: 1px solid #f97316;
  outline: none;
`;

const Button = styled.button`
  padding: 10px 16px;
  margin-right: 10px;
  background: linear-gradient(90deg, #ff7a2a 0%, #ffb06b 100%);
  color: #fff;
  font-weight: 700;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }
`;

const CreateSurveyModal = ({ isOpen, onClose, onSave, routeId, users }) => {
  const [selectedSurveyors, setSelectedSurveyors] = useState([]);
  const [travelMode, setTravelMode] = useState("walk");
  const [category, setCategory] = useState("Data Collect");
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setSelectedSurveyors([]);
      setTravelMode("walk");
      setCategory("Data Collect");
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (selectedSurveyors.length === 0) {
      alert("Please select at least one surveyor");
      return;
    }

    try {
      setLoading(true);
      const surveyorIds = selectedSurveyors.map((s) => s.value);
      const payload = {
        surveyor: surveyorIds,
        travelMode: travelMode,
        category: category,
      };

      await api.post(`/api/surveys/${routeId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Survey created successfully!");
      onClose();
      if (onSave) onSave();
    } catch (err) {
      console.error("Create survey error:", err.response?.data || err);
      alert(err.response?.data?.message || "Failed to create survey");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Transform users for react-select
  const surveyorOptions =
    users?.map((user) => ({
      value: user._id,
      label: `${user.name}${user.email ? ` (${user.email})` : ""}`,
    })) || [];

  return (
    <Overlay>
      <ModalBox>
        <Title>Create Survey</Title>
        <CloseBtn onClick={onClose}>✖</CloseBtn>

        <Section>
          <Label>Select Surveyors</Label>
          <Select
            isMulti
            options={surveyorOptions}
            value={selectedSurveyors}
            onChange={setSelectedSurveyors}
            placeholder="Select one or more surveyors"
            isSearchable
            closeMenuOnSelect={false}
          />
        </Section>

        <Section>
          <Label>Category</Label>
          <StyledSelect
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="Data Collect">Data Collection</option>
            <option value="Vaccination">Vaccination</option>
          </StyledSelect>
        </Section>

        <Section>
          <Label>Travel Mode</Label>
          <StyledSelect
            value={travelMode}
            onChange={(e) => setTravelMode(e.target.value)}
          >
            <option value="walk">Walk</option>
            <option value="bike">Bike</option>
            <option value="car">Car</option>
          </StyledSelect>
        </Section>

        <div>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? "Creating..." : "Create Survey"}
          </Button>
          <Button
            style={{ background: "#ef4444" }}
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </ModalBox>
    </Overlay>
  );
};

export default CreateSurveyModal;
